import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Badge } from '@/components/ui'
import { Skeleton } from '@/components/ui'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '@/components/ui'
import { cn } from '@/utils'
import type { AvatarInfo } from '@/connectors/avatar'
import { Search, Check, User, Play, Video } from 'lucide-react'

interface ReplicaSelectorProps {
  replicas: AvatarInfo[]
  selectedReplicaId?: string
  onSelect: (replica: AvatarInfo) => void
  isLoading?: boolean
  className?: string
}

export function ReplicaSelector({
  replicas,
  selectedReplicaId,
  onSelect,
  isLoading = false,
  className,
}: ReplicaSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [previewingId, setPreviewingId] = useState<string | null>(null)

  const filteredReplicas = replicas.filter(
    (replica) =>
      replica.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      replica.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const selectedReplica = replicas.find((r) => r.id === selectedReplicaId)

  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-2 md:grid-cols-3 gap-4', className)}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="aspect-video rounded-lg" />
        ))}
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search avatars..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Selected replica display */}
      {selectedReplica && (
        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-3">
            {selectedReplica.previewUrl ? (
              <img
                src={selectedReplica.previewUrl}
                alt={selectedReplica.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span className="font-medium text-primary">
                  Selected: {selectedReplica.name}
                </span>
              </div>
              {selectedReplica.description && (
                <p className="text-sm text-primary">
                  {selectedReplica.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Replica grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {filteredReplicas.map((replica) => {
            const isSelected = replica.id === selectedReplicaId
            const isPreviewing = previewingId === replica.id

            return (
              <motion.button
                key={replica.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={() => onSelect(replica)}
                className={cn(
                  'relative group rounded-lg overflow-hidden border-2 transition-all',
                  'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
                  isSelected
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-transparent hover:border-muted-foreground/40'
                )}
              >
                {/* Preview image/video */}
                <div className="aspect-video bg-muted relative">
                  {replica.previewUrl ? (
                    <>
                      {isPreviewing ? (
                        <video
                          src={replica.previewUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                          onEnded={() => setPreviewingId(null)}
                        />
                      ) : (
                        <img
                          src={replica.previewUrl}
                          alt={replica.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    {replica.previewUrl && !isPreviewing && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileHover={{ scale: 1.1 }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-full bg-white/90 text-dark-900"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPreviewingId(replica.id)
                        }}
                      >
                        <Play className="w-5 h-5" />
                      </motion.button>
                    )}
                  </div>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 p-1 rounded-full bg-primary text-primary-foreground">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-2 bg-card text-left">
                  <p className="font-medium text-foreground text-sm truncate">
                    {replica.name}
                  </p>
                  {replica.gender && (
                    <Badge variant="secondary" className="text-xs mt-1">
                      {replica.gender}
                    </Badge>
                  )}
                </div>
              </motion.button>
            )
          })}
        </AnimatePresence>

        {filteredReplicas.length === 0 && (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            No avatars found matching "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  )
}

// Modal version for use in dialogs
interface ReplicaSelectorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  replicas: AvatarInfo[]
  selectedReplicaId?: string
  onSelect: (replica: AvatarInfo) => void
  isLoading?: boolean
}

export function ReplicaSelectorModal({
  open,
  onOpenChange,
  replicas,
  selectedReplicaId,
  onSelect,
  isLoading,
}: ReplicaSelectorModalProps) {
  const [tempSelectedId, setTempSelectedId] = useState(selectedReplicaId)

  // Reset temp selection when modal opens
  useEffect(() => {
    if (open) {
      setTempSelectedId(selectedReplicaId)
    }
  }, [open, selectedReplicaId])

  const handleConfirm = () => {
    const replica = replicas.find((r) => r.id === tempSelectedId)
    if (replica) {
      onSelect(replica)
    }
    onOpenChange(false)
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="xl">
        <ModalHeader>
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            <ModalTitle>Select Avatar</ModalTitle>
          </div>
          <ModalDescription>
            Choose a Tavus replica for your AI expert. Hover over avatars to preview.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <ReplicaSelector
            replicas={replicas}
            selectedReplicaId={tempSelectedId}
            onSelect={(replica) => setTempSelectedId(replica.id)}
            isLoading={isLoading}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!tempSelectedId}>
            Confirm Selection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
